// Site-wide editable settings (contact info, social links, announcement bar,
// impact numbers, membership policy and fees, maintenance mode). Values live
// in the `site_settings` table and are edited in the admin backend; the public
// pages read them through `useSiteSettings()`, which falls back to the
// built-in defaults until the table has been loaded (or when it does not
// exist yet).

import { useEffect, useSyncExternalStore } from "react";
import { supabase } from "@/lib/supabase";
import { contactInfo } from "@/mocks/contactData";

export interface ContactSettings {
  legal_name: string;
  registration: string;
  address: string;
  email: string;
  phone: string;
  community_url: string;
  map_embed_url: string;
}

export interface SocialSettings {
  facebook_url: string;
  linkedin_url: string;
  youtube_url: string;
}

export interface AnnouncementSettings {
  enabled: boolean;
  text: string;
  link_url: string;
  link_label: string;
}

export interface ImpactStat {
  label: string;
  value: string;
  suffix: string;
}

export interface ImpactSettings {
  enabled: boolean;
  stats: ImpactStat[];
}

export interface MembershipFees {
  personal: number;
  group: number;
  corporate: number;
}

export interface MembershipSettings {
  fees: MembershipFees;
  /**
   * Whether visitors can submit the on-line application form. `false` (the
   * default) means membership is by invitation only: the public site shows
   * how to get in touch instead of the form, and the database refuses
   * anonymous inserts into `membership_applications`.
   */
  applications_open: boolean;
}

export interface MaintenanceSettings {
  /**
   * When enabled the public site is "taken down": every public route renders
   * the maintenance page. The admin backend (/admin) keeps working so the
   * association can maintain content and upload data before going live.
   */
  enabled: boolean;
  title: string;
  message: string;
}

export interface SiteSettings {
  contact: ContactSettings;
  social: SocialSettings;
  announcement: AnnouncementSettings;
  impact: ImpactSettings;
  membership: MembershipSettings;
  maintenance: MaintenanceSettings;
}

export const DEFAULT_SITE_SETTINGS: SiteSettings = {
  contact: {
    legal_name: contactInfo.legalName,
    registration: contactInfo.registration,
    address: contactInfo.address,
    email: contactInfo.email,
    phone: "",
    community_url: contactInfo.communityUrl,
    map_embed_url: contactInfo.mapEmbedUrl,
  },
  social: {
    facebook_url: "",
    linkedin_url: "",
    youtube_url: "",
  },
  announcement: {
    enabled: false,
    text: "",
    link_url: "",
    link_label: "",
  },
  impact: {
    enabled: false,
    stats: [
      { label: "產業論壇", value: "", suffix: "場" },
      { label: "工作坊與講座", value: "", suffix: "場" },
      { label: "產業研究報告", value: "", suffix: "份" },
      { label: "國際合作組織", value: "", suffix: "個" },
    ],
  },
  membership: {
    fees: { personal: 2000, group: 5000, corporate: 20000 },
    applications_open: false,
  },
  maintenance: {
    enabled: false,
    title: "網站建置中",
    message: "TAIAA 台灣 AI 應用推廣協會官方網站正在建置與資料整理中，敬請期待。如有任何問題，歡迎來信與我們聯繫。",
  },
};

export const SETTING_KEYS = ["contact", "social", "announcement", "impact", "membership", "maintenance"] as const;
export type SettingKey = (typeof SETTING_KEYS)[number];

// ---- sanitising merge -------------------------------------------------------

const str = (v: unknown, fallback: string): string => (typeof v === "string" ? v : fallback);
const bool = (v: unknown, fallback: boolean): boolean => (typeof v === "boolean" ? v : fallback);
const num = (v: unknown, fallback: number): number => {
  const n = typeof v === "number" ? v : typeof v === "string" && v.trim() !== "" ? Number(v) : NaN;
  return Number.isFinite(n) && n >= 0 ? n : fallback;
};

type Raw = Record<string, unknown> | null | undefined;

function mergeContact(raw: Raw, d: ContactSettings): ContactSettings {
  const r = raw ?? {};
  return {
    legal_name: str(r.legal_name, d.legal_name),
    registration: str(r.registration, d.registration),
    address: str(r.address, d.address),
    email: str(r.email, d.email),
    phone: str(r.phone, d.phone),
    community_url: str(r.community_url, d.community_url),
    map_embed_url: str(r.map_embed_url, d.map_embed_url),
  };
}

function mergeSocial(raw: Raw, d: SocialSettings): SocialSettings {
  const r = raw ?? {};
  return {
    facebook_url: str(r.facebook_url, d.facebook_url),
    linkedin_url: str(r.linkedin_url, d.linkedin_url),
    youtube_url: str(r.youtube_url, d.youtube_url),
  };
}

function mergeAnnouncement(raw: Raw, d: AnnouncementSettings): AnnouncementSettings {
  const r = raw ?? {};
  return {
    enabled: bool(r.enabled, d.enabled),
    text: str(r.text, d.text),
    link_url: str(r.link_url, d.link_url),
    link_label: str(r.link_label, d.link_label),
  };
}

function mergeImpact(raw: Raw, d: ImpactSettings): ImpactSettings {
  const r = raw ?? {};
  const list = Array.isArray(r.stats) ? (r.stats as unknown[]) : null;
  const stats: ImpactStat[] = list
    ? list.slice(0, 6).map((item, i) => {
        const s = (item ?? {}) as Record<string, unknown>;
        const fallback = d.stats[i] ?? { label: "", value: "", suffix: "" };
        return {
          label: str(s.label, fallback.label),
          value: str(s.value, fallback.value),
          suffix: str(s.suffix, fallback.suffix),
        };
      })
    : d.stats.map((s) => ({ ...s }));
  return { enabled: bool(r.enabled, d.enabled), stats };
}

function mergeMembership(raw: Raw, d: MembershipSettings): MembershipSettings {
  const r = raw ?? {};
  const fees = (r.fees ?? {}) as Record<string, unknown>;
  return {
    fees: {
      personal: num(fees.personal, d.fees.personal),
      group: num(fees.group, d.fees.group),
      corporate: num(fees.corporate, d.fees.corporate),
    },
    applications_open: bool(r.applications_open, d.applications_open),
  };
}

function mergeMaintenance(raw: Raw, d: MaintenanceSettings): MaintenanceSettings {
  const r = raw ?? {};
  return {
    enabled: bool(r.enabled, d.enabled),
    title: str(r.title, d.title),
    message: str(r.message, d.message),
  };
}

/** Merge rows from `site_settings` over the defaults, ignoring malformed values. */
export function mergeSiteSettings(
  rows: { key: string; value: unknown }[] | null | undefined,
  defaults: SiteSettings = DEFAULT_SITE_SETTINGS
): SiteSettings {
  const byKey: Record<string, Raw> = {};
  for (const row of rows ?? []) {
    if (row && typeof row.key === "string" && row.value && typeof row.value === "object" && !Array.isArray(row.value)) {
      byKey[row.key] = row.value as Record<string, unknown>;
    }
  }
  return {
    contact: mergeContact(byKey.contact, defaults.contact),
    social: mergeSocial(byKey.social, defaults.social),
    announcement: mergeAnnouncement(byKey.announcement, defaults.announcement),
    impact: mergeImpact(byKey.impact, defaults.impact),
    membership: mergeMembership(byKey.membership, defaults.membership),
    maintenance: mergeMaintenance(byKey.maintenance, defaults.maintenance),
  };
}

// ---- tiny external store -----------------------------------------------------

let current: SiteSettings = DEFAULT_SITE_SETTINGS;
let loaded = false;
let inflight: Promise<SiteSettings> | null = null;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getSiteSettingsSnapshot(): SiteSettings {
  return current;
}

export function loadSiteSettings(force = false): Promise<SiteSettings> {
  if (loaded && !force) return Promise.resolve(current);
  if (inflight && !force) return inflight;
  inflight = (async () => {
    try {
      const { data, error } = await supabase.from("site_settings").select("key,value");
      if (!error && data) {
        current = mergeSiteSettings(data as { key: string; value: unknown }[]);
      }
    } catch {
      // Network failure or missing table: keep defaults.
    }
    loaded = true;
    inflight = null;
    emit();
    return current;
  })();
  return inflight;
}

/** Force a reload (used by the admin after saving settings). */
export function invalidateSiteSettings(): Promise<SiteSettings> {
  return loadSiteSettings(true);
}

/** Test helper: reset the store to defaults. */
export function __resetSiteSettingsStore(): void {
  current = DEFAULT_SITE_SETTINGS;
  loaded = false;
  inflight = null;
  emit();
}

export function useSiteSettings(): { settings: SiteSettings; loaded: boolean } {
  const settings = useSyncExternalStore(subscribe, getSiteSettingsSnapshot, getSiteSettingsSnapshot);
  const isLoaded = useSyncExternalStore(
    subscribe,
    () => loaded,
    () => false
  );
  useEffect(() => {
    if (!loaded) void loadSiteSettings();
  }, []);
  return { settings, loaded: isLoaded };
}

/** Title / message actually shown on the maintenance page (blank fields fall back to the defaults). */
export function maintenanceCopy(m: MaintenanceSettings): { title: string; message: string } {
  return {
    title: m.title.trim() || DEFAULT_SITE_SETTINGS.maintenance.title,
    message: m.message.trim() || DEFAULT_SITE_SETTINGS.maintenance.message,
  };
}

/** Format a fee number as "2,000". */
export function formatFee(n: number): string {
  return new Intl.NumberFormat("zh-TW").format(n);
}
